import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControlLabel, 
    Switch, 
    CircularProgress, 
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
    Chip,
    IconButton,
    Avatar,
    Divider,
    Paper,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { RestaurantService } from '../../../api/services/restaurantService';
import { MediaService } from '../../../api/services/mediaService';
import { toast } from '../../../utils/toast';
import FileUploadDropzone from '../../../components/FileUploadDropzone';

const DIETARY_OPTIONS = [
    'Veg',
    'Vegan',
    'Gluten-Free',
    'Nut-Allergy',
    'Spicy',
    'Dairy-Free',
    'Halal',
    'Keto'
];

export default function EditMenuItemModal({ open, onClose, item, onItemUpdated }) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [categories, setCategories] = useState([]);
    
    const [form, setForm] = useState({
        categoryId: '',
        name: '',
        price: '',
        description: '',
        image: '',
        dietaryTags: [],
        preparationTime: '',
        isAvailable: true,
        isActive: true,
        isAlcohol: false
    });

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
    }, [open]);

    useEffect(() => {
        if (item && open) {
            setForm({
                categoryId: item.categoryId || '',
                name: item.name || '',
                price: item.price !== undefined ? item.price : (item.basePrice || ''),
                description: item.description || '',
                image: item.image || '',
                dietaryTags: Array.isArray(item.dietaryTags) ? item.dietaryTags : (item.dietaryTags ? item.dietaryTags.split(',') : []),
                preparationTime: item.preparationTime || '',
                isAvailable: Boolean(item.isAvailable ?? true),
                isActive: Boolean(item.isActive ?? true),
                isAlcohol: Boolean(item.isAlcohol)
            });
        }
    }, [item, open]);

    const fetchCategories = async () => {
        try {
            const response = await RestaurantService.getMenuCategories({ noPagination: true });
            const dataArray = response?.data?.data || response?.data || response?.categories || [];
            setCategories(Array.isArray(dataArray) ? dataArray : []);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleClose = () => {
        if (isUpdating) return;
        onClose();
    };

    const handleSubmit = async () => {
        if (!item?.id || !form.name.trim() || !form.categoryId || form.price === '') {
            toast.error('Please fill in all required fields (Name, Category, Price)');
            return;
        }
        
        setIsUpdating(true);
        try {
            await RestaurantService.updateMenuItem(item.id, {
                categoryId: form.categoryId,
                name: form.name.trim(),
                price: parseFloat(form.price),
                description: form.description.trim(),
                image: form.image.trim(),
                dietaryTags: form.dietaryTags,
                preparationTime: parseInt(form.preparationTime || 0, 10),
                isAvailable: Boolean(form.isAvailable),
                isActive: Boolean(form.isActive),
                isAlcohol: Boolean(form.isAlcohol)
            });
            toast.success('Menu item updated successfully');
            handleClose();
            if (onItemUpdated) {
                onItemUpdated();
            }
        } catch (error) {
            console.error('Failed to update menu item:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDietaryChange = (event) => {
        const { target: { value } } = event;
        setForm({
            ...form,
            dietaryTags: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);
        try {
            const result = await MediaService.uploadFile(file, (progress) => {
                setUploadProgress(progress);
            });
            const imageUrl = result?.url || result?.publicUrl || result;
            setForm({ ...form, image: imageUrl });
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Failed to upload image:', error);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleRemoveImage = () => {
        setForm({ ...form, image: '' });
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                Edit Menu Item
                <IconButton onClick={handleClose} disabled={isUpdating} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Item Name"
                            placeholder="e.g., Pancakes"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            disabled={isUpdating}
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required disabled={isUpdating}>
                            <InputLabel>Menu Category</InputLabel>
                            <Select
                                value={form.categoryId}
                                label="Menu Category"
                                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Base Price"
                            placeholder="0.00"
                            value={form.price}
                            onChange={(e) => setForm({ ...form, price: Math.max(0, parseFloat(e.target.value || 0)) })}
                            disabled={isUpdating}
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Preparation Time (mins)"
                            placeholder="e.g., 15"
                            value={form.preparationTime}
                            onChange={(e) => setForm({ ...form, preparationTime: Math.max(0, parseInt(e.target.value || 0, 10)) })}
                            disabled={isUpdating}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Item Description"
                            placeholder="Customer-facing description of the dish"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            disabled={isUpdating}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Item Photo</Typography>
                        </Divider>
                    </Grid>
                    
                    <Grid item xs={12}>
                        {form.image ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar
                                        src={form.image}
                                        variant="rounded"
                                        sx={{ width: 100, height: 100, border: '2px solid', borderColor: 'divider' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={handleRemoveImage}
                                        disabled={isUpdating}
                                        sx={{
                                            position: 'absolute', top: -10, right: -10,
                                            bgcolor: 'error.main', color: 'white',
                                            '&:hover': { bgcolor: 'error.dark' },
                                            width: 24, height: 24
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                                <Box>
                                    <Button
                                        variant="outlined" size="small" color="error"
                                        disabled={isUpdating}
                                        onClick={handleRemoveImage}
                                    >
                                        Remove Image
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <FileUploadDropzone 
                                accept="image/png,image/jpeg" 
                                maxSizeMB={2}
                                onUploadSuccess={(url) => {
                                    setForm(prev => ({ ...prev, image: url }));
                                }} 
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth disabled={isUpdating}>
                            <InputLabel>Dietary Tags</InputLabel>
                            <Select
                                multiple
                                value={form.dietaryTags}
                                onChange={handleDietaryChange}
                                input={<OutlinedInput label="Dietary Tags" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {DIETARY_OPTIONS.map((tag) => (
                                    <MenuItem key={tag} value={tag}>
                                        {tag}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.01)', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isAvailable}
                                        onChange={(e) => setForm({ ...form, isAvailable: e.target.checked })}
                                        color="success"
                                        disabled={isUpdating}
                                    />
                                }
                                label="Operational (In-Stock)"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                        color="success"
                                        disabled={isUpdating}
                                    />
                                }
                                label="Active (Publish)"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isAlcohol}
                                        onChange={(e) => setForm({ ...form, isAlcohol: e.target.checked })}
                                        color="error"
                                        disabled={isUpdating}
                                    />
                                }
                                label="Contains Alcohol (Adults)"
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={isUpdating} 
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit}
                    disabled={isUpdating || !form.name.trim() || !form.categoryId || form.price === ''}
                    startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isUpdating ? 'Saving...' : 'Update Item'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
